import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, forwardRef, useContext, useEffect, useId, useRef } from 'react';
import { cn } from '../utils/cn';
/* DialogTitle meldet seine id, damit das <dialog> per aria-labelledby einen
   barrierefreien Namen bekommt (wie ModalTitle beim Modal). */
const DialogTitleIdContext = createContext(undefined);
/**
 * Modaler Dialog (MUI-`Dialog`-Ersatz) auf Basis des nativen `<dialog>`.
 * Liefert Fokus-Trap, Escape-Handling und `::backdrop` ohne Zusatz-Logik;
 * faellt auf das `open`-Attribut zurueck, wenn `showModal` fehlt (z. B. jsdom).
 * Ein Backdrop-Klick schliesst nur, wenn die Geste auch auf dem Backdrop
 * begonnen hat — eine Textauswahl, die aus dem Inhalt hinausgezogen wird,
 * schliesst den Dialog nicht.
 */
export const Dialog = forwardRef(function Dialog({ open, onClose, className, children, ...rest }, forwardedRef) {
    const innerRef = useRef(null);
    const pointerOnBackdrop = useRef(false);
    const pointerSeen = useRef(false);
    const titleId = useId();
    const setRefs = (node) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function')
            forwardedRef(node);
        else if (forwardedRef)
            forwardedRef.current = node;
    };
    useEffect(() => {
        const d = innerRef.current;
        if (!d)
            return;
        if (open && !d.open) {
            try {
                d.showModal();
            }
            catch {
                d.setAttribute('open', '');
            }
        }
        else if (!open && d.open) {
            try {
                d.close();
            }
            catch {
                d.removeAttribute('open');
            }
        }
    }, [open]);
    const handleCancel = (e) => {
        e.preventDefault();
        onClose?.();
    };
    const handlePointerDown = (e) => {
        pointerOnBackdrop.current = e.target === innerRef.current;
    };
    const handleClick = (e) => {
        // Klick auf das <dialog>-Element selbst = Backdrop (Inhalt sind Kinder).
        // Nur schliessen, wenn auch der pointerdown auf dem Backdrop lag (oder es
        // keinen gab, z. B. synthetischer Klick in Tests).
        const startedOnBackdrop = pointerOnBackdrop.current || !pointerSeen.current;
        pointerOnBackdrop.current = false;
        pointerSeen.current = false;
        if (e.target === innerRef.current && startedOnBackdrop)
            onClose?.();
    };
    // className nur im offenen Zustand: Layout-Utilities (flex, grid) wuerden
    // sonst das display:none des geschlossenen <dialog> ueberschreiben. (Der
    // Kommentar steht hier, nicht zwischen den Props: tsc gibt ihn sonst nach
    // "ref: setRefs, " aus, und dist/ samt allen Kopien traegt ein Leerzeichen
    // am Zeilenende.)
    return (_jsx("dialog", { ref: setRefs, className: cn('sec-dialog', open && className), "aria-labelledby": rest['aria-label'] ? undefined : titleId, onCancel: handleCancel, onPointerDown: (e) => {
            pointerSeen.current = true;
            handlePointerDown(e);
        }, onClick: handleClick, ...rest, children: _jsx(DialogTitleIdContext.Provider, { value: titleId, children: children }) }));
});
export const DialogTitle = forwardRef(function DialogTitle({ className, id, ...rest }, ref) {
    const ctxId = useContext(DialogTitleIdContext);
    return _jsx("h2", { ref: ref, id: id ?? ctxId, className: cn('sec-dialog__title', className), ...rest });
});
export const DialogContent = forwardRef(function DialogContent({ className, ...rest }, ref) {
    return _jsx("div", { ref: ref, className: cn('sec-dialog__content', className), ...rest });
});
export const DialogContentText = forwardRef(function DialogContentText({ className, ...rest }, ref) {
    return _jsx("p", { ref: ref, className: cn('sec-dialog__content-text', className), ...rest });
});
export const DialogActions = forwardRef(function DialogActions({ className, ...rest }, ref) {
    return _jsx("div", { ref: ref, className: cn('sec-dialog__actions', className), ...rest });
});
